// third-party
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { filter } from 'lodash';

// models and services
import { AccountModel, AccountsDict } from 'src/types/account.model';
import AccountService from 'src/services/account.service';
import { ExpensesViewModel } from 'src/types/view-models/onboarding/expenses.view.model';
import FormatService from 'src/services/format.service';
import LiteralsService from 'src/services/literals/LiteralsService';
import { RecurringExpenseModel } from 'src/types/recurring-expense.model';
import RecurringExpenseService from 'src/services/recurring-expense.service';

// components
import { ButtonPrimary, IconCards } from '@americanexpress/dls-react';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import ToggleAccountRow from 'src/components/atomic/atoms/toggleAccountRow/ToggleAccountRow';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import Drawer from 'src/components/drawer/Drawer';
import Output from 'src/components/atomic/molecules/output/Output';
import OnboardingHeader from 'src/components/onboarding-header/OnboardingHeader';
import SectionHeader from 'src/components/header/SectionHeader';

// icons and styles
import colors from 'src/styles/colors.module.scss';
import styles from './expenses.module.scss';
import Callout from '../../../../atoms/callout/Callout';
import { DLSSize } from 'src/types/dls-size.enum';
import WhyShouldYouConnectTooltip from '../../../../atoms/tooltips/whyYouShouldConnectTooltip/WhyShouldYouConnectTooltip';
import Selectors from 'src/utils/selectors';

/** Form validation schema */
const validationSchema = Yup.object({
    additionalMonthlySpending: Yup.number().nullable().typeError('Must be a number')
});

export default function Expenses({
    user,
    back,
    drawerProps,
    handleReloadUser,
    setExpenses,
    persons,
    expensesNext
}: ExpensesViewModel): JSX.Element {
    /** Dictionary of expense, non-expense and montly average expenses for quick lookups */
    const [accountsDict, setAccountsDict] = useState<AccountsDict>();
    const [nonExpenseAccountsDict, setNonExpenseAccountDict] = useState<AccountsDict>();
    const [avgExpenses, setAvgExpenses] = useState<number>();
    const [additionalMonthlySpending, setAdditionalMonthlySpending] = useState<number>();

    /** Ordered list of accountIds */
    const [accountsOrder, setAccountsOrder] = useState<string[]>();

    /** Toggle visible/hidden account, then repopulate account data */
    const toggleAccountVisibility = useCallback(async (account: AccountModel, hidden: boolean): Promise<void> => {
        if (hidden) await AccountService.showAccount(account.accountId);
        else await AccountService.hideAccount(account.id);
        await handleReloadUser();
        const _accountsDict = await AccountService.getExpenseAccountsDict(user);
        setAccountsDict(_accountsDict);
    }, []);

    /** When user enters the additional monthly spending, we need to add this as a recurring expense */
    const handleInputBlur = useCallback(async (value: number): Promise<void> => {
        await setExpenses(value, 'Additional');
    }, []);

    /** Submitting should take you to the next page */
    const onSubmit = useCallback(async (): Promise<void> => {
        await expensesNext();
    }, []);

    /** When plaid is connected, user.accounts will be populated, and we will need to add these accounts */
    useEffect(() => {
        void (async () => {
            const _accountsDict = await AccountService.getExpenseAccountsDict(user);
            if (_accountsDict) setAccountsDict(_accountsDict);
            const noOfAccountsBefore: number = accountsDict ? Object.keys(accountsDict).length : 0;
            const noOfAccountsAfter: number = Object.keys(_accountsDict).length;
            if ((!accountsOrder && _accountsDict) || (accountsOrder && noOfAccountsBefore !== noOfAccountsAfter)) {
                setAccountsOrder(Object.keys(_accountsDict));
            }
            const _nonExpenseAccountsDict = await AccountService.getNonExpenseAccountsDict(user);
            if (_nonExpenseAccountsDict) setNonExpenseAccountDict(_nonExpenseAccountsDict);
        })();
    }, [user.accounts]);

    /** When plaid is connected, user.accounts will be populated, and we will need to calculate the average
     *  monthly spending from the accounts. We will also need to add the additional monthly
     *  spending, the user enters, into the average monthly spending */
    useEffect(() => {
        void (async () => {
            const recurringExpenses: RecurringExpenseModel[] = await RecurringExpenseService.getRecurringExpenses();
            const _additionalMonthlySpending: number =
                filter(recurringExpenses, (recurringExpense) => {
                    return recurringExpense.name === 'Additional';
                })[0]?.monthlyAmount || 0;
            setAdditionalMonthlySpending(_additionalMonthlySpending);
            const _avgExpenses = AccountService.getAverageExpenses(user) || 0;
            setAvgExpenses(_avgExpenses);
        })();
    }, [user.cashFlow]);

    return (
        <Formik
            initialValues={{ additionalMonthlySpending }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ values }) => (
                <Form>
                    <Drawer
                        className={styles.onboardingExpenses}
                        drawerProps={drawerProps}
                        title={LiteralsService.getLiteral('Onboarding.expenses.title')}
                        handleBack={back}
                        withBackButton
                    >
                        <OnboardingHeader
                            description={LiteralsService.getLiteral('Onboarding.expenses.description')}
                            onboardingStep={2}
                        />
                        <div className={styles.accounts}>
                            <Output
                                title={LiteralsService.getLiteral('Onboarding.expenses.outputHeading')}
                                main={FormatService.formatCurrency(avgExpenses, { round: true })}
                                tooltipText={LiteralsService.getLiteral('Onboarding.expenses.needInfo')}
                            />
                            {accountsOrder?.length > 0 && (
                                <div className={styles.connectedAccountsSection}>
                                    <SectionHeader
                                        title={LiteralsService.getLiteral('Onboarding.expenses.accountsTitle')}
                                        subtitle={LiteralsService.getLiteral('Onboarding.expenses.accountsSubtitle')}
                                        tooltipDescription={LiteralsService.getLiteral(
                                            'Onboarding.expenses.accountsTooltip'
                                        )}
                                    />
                                    {accountsOrder.map((e, i) => {
                                        const { account, hidden } = accountsDict[e];
                                        return (
                                            <ToggleAccountRow
                                                title={account.name}
                                                subtitle={account.institution.name}
                                                key={`toggleAccountRow-${i}`}
                                                hidden={hidden}
                                                onClick={async () => {
                                                    await toggleAccountVisibility(account, hidden);
                                                }}
                                            />
                                        );
                                    })}
                                    {nonExpenseAccountsDict && Object.keys(nonExpenseAccountsDict).length > 0 && (
                                        <Typography variant={TypographyVariants.CAPTION} className={colors.textWeak}>
                                            {LiteralsService.getLiteral('Onboarding.extraAccounts', [
                                                Object.keys(nonExpenseAccountsDict).length,
                                                'Expense'
                                            ])}
                                        </Typography>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.gridSection}>
                            <Typography className={styles.gridTitle} variant={TypographyVariants.BODY_BOLD}>
                                {LiteralsService.getLiteral('Onboarding.expenses.additionalMonthlySpendingTitle', [
                                    persons?.length > 1 ? "family's" : ''
                                ])}
                            </Typography>
                            <Typography className={styles.gridDescription} variant={TypographyVariants.BODY}>
                                {LiteralsService.getLiteral('Onboarding.expenses.additionalMonthlySpendingDesc')}
                            </Typography>
                            <CurrencyInput
                                id="additionalMonthlySpending"
                                handleInputBlur={handleInputBlur}
                                label={LiteralsService.getLiteral(
                                    'Onboarding.expenses.additionalMonthlySpendingTitle',
                                    [persons?.length > 1 ? "family's" : '']
                                )}
                                hideLabel
                            />
                        </div>

                        <Callout
                            icon={<IconCards size={DLSSize.MEDIUM} />}
                            heading={LiteralsService.getLiteral('Onboarding.expenses.connectAccountTitle')}
                        >
                            <Typography variant={TypographyVariants.BODY}>
                                {LiteralsService.getLiteral('Onboarding.expenses.connectAccountDesc')}
                            </Typography>
                            <WhyShouldYouConnectTooltip withText />
                        </Callout>

                        <div>
                            <ButtonPrimary type="submit" data-cy={Selectors.PlanAhead.NEXT_BUTTON}>
                                {LiteralsService.getLiteral('Onboarding.next')}
                            </ButtonPrimary>
                        </div>
                    </Drawer>
                </Form>
            )}
        </Formik>
    );
}
